import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabaseClient';

interface Props {
  companyId: string;
  onNavigate: (tab: string) => void;
}

interface ChecklistStep {
  id: string;
  label: string;
  tab: string;
  completed: boolean;
}

const DISMISS_KEY = 'ss_checklist_dismissed';

export default function OnboardingChecklist({ companyId, onNavigate }: Props) {
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === 'true');
  const [steps, setSteps] = useState<ChecklistStep[]>([]);

  const loadProgress = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);

    // El checklist solo se muestra a clientes normales (ver Dashboard.tsx),
    // así que los pasos de módulos SSO/Cumplimiento (ocultos para clientes) se omiten.
    const [
      { count: productsCount },
      { count: warehousesCount },
    ] = await Promise.all([
      supabase.from('products').select('id', { count: 'exact', head: true }).eq('company_id', companyId),
      supabase.from('warehouses').select('id', { count: 'exact', head: true }).eq('company_id', companyId),
    ]);

    setSteps([
      { id: 'productos',    label: 'Agrega tu primer producto químico',  tab: 'productos',    completed: (productsCount ?? 0) > 0 },
      { id: 'bodegas',      label: 'Crea tu primera bodega',              tab: 'bodegas',      completed: (warehousesCount ?? 0) > 0 },
    ]);
    setLoading(false);
  }, [companyId]);

  useEffect(() => { loadProgress(); }, [loadProgress]);

  const completedCount = steps.filter((s) => s.completed).length;

  useEffect(() => {
    if (steps.length > 0 && completedCount === steps.length) {
      localStorage.setItem(DISMISS_KEY, 'true');
      setDismissed(true);
    }
  }, [completedCount, steps.length]);

  if (dismissed || loading || steps.length === 0) return null;

  function handleDismiss() {
    localStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
  }

  return (
    <div style={card}>
      <div style={header}>
        <div>
          <h3 style={title}>🚀 Primeros pasos</h3>
          <p style={subtitle}>{completedCount} de {steps.length} completados</p>
        </div>
        <button style={closeBtn} onClick={handleDismiss} aria-label="Cerrar checklist">×</button>
      </div>

      <div style={progressTrack}>
        <div style={{ ...progressFill, width: `${(completedCount / steps.length) * 100}%` }} />
      </div>

      <div style={list}>
        {steps.map((step) => (
          <button
            key={step.id}
            style={{ ...stepRow, ...(step.completed ? stepRowDone : {}) }}
            onClick={() => onNavigate(step.tab)}
          >
            <span style={stepIcon}>{step.completed ? '✅' : '⬜'}</span>
            <span style={{ ...stepLabel, ...(step.completed ? stepLabelDone : {}) }}>{step.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const card: React.CSSProperties = {
  background: '#fff',
  borderLeft: '4px solid #2563eb',
  borderRadius: 12,
  padding: '20px 24px',
  margin: '0 24px 20px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
};

const header: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
};

const title: React.CSSProperties = {
  margin: 0,
  fontSize: '1.05rem',
  fontWeight: 700,
  color: '#0f172a',
};

const subtitle: React.CSSProperties = {
  margin: '4px 0 0',
  fontSize: '0.82rem',
  color: '#64748b',
};

const closeBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#94a3b8',
  fontSize: '1.3rem',
  lineHeight: 1,
  cursor: 'pointer',
  padding: 2,
};

const progressTrack: React.CSSProperties = {
  marginTop: 12,
  height: 6,
  borderRadius: 3,
  background: '#e2e8f0',
  overflow: 'hidden',
};

const progressFill: React.CSSProperties = {
  height: '100%',
  background: '#2563eb',
  borderRadius: 3,
  transition: 'width 0.3s ease',
};

const list: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginTop: 16,
};

const stepRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  textAlign: 'left',
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  padding: '10px 14px',
  cursor: 'pointer',
  fontSize: '0.88rem',
};

const stepRowDone: React.CSSProperties = {
  background: '#f0fdf4',
  borderColor: '#bbf7d0',
};

const stepIcon: React.CSSProperties = {
  fontSize: '1rem',
  flexShrink: 0,
};

const stepLabel: React.CSSProperties = {
  color: '#1e293b',
  fontWeight: 600,
};

const stepLabelDone: React.CSSProperties = {
  color: '#64748b',
  textDecoration: 'line-through',
};
